import React, { useEffect, useRef, useState } from "react";
import { navSections } from "../data/reportData";

// wide / full-bleed report content the nav must never sit on top of
const CONTENT_SELECTORS = [
  "#problem-stack",
  "[data-testid='builders-sides']",
  "[data-testid='integration-brands']",
  "[data-testid='industry-ticker']",
  "[data-testid='industries-window']",
  "[data-testid='stories-dome']",
  "[data-testid='reading-stack']",
];

// graphs: hide the nav whenever one shares the nav's vertical band, even without direct overlap
const CHART_SELECTORS = [
  "[data-testid='coping-chart']",
  "[data-testid='price-range-callout']",
  "[data-testid='blocked-chart']",
  "[data-testid='integration-chart']",
  "[data-testid='unlocked-chart']",
  "[data-testid^='money-signals']",
  "[data-testid^='behavior-signals']",
];

export default function SideNav() {
  const navRef = useRef(null);
  const [active, setActive] = useState(navSections[0].id);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("top");
      const heroEnd = hero ? hero.offsetTop + hero.offsetHeight - 200 : window.innerHeight * 0.85;
      const nearBottom =
        window.innerHeight + window.scrollY > document.body.scrollHeight - 700;

      // hide whenever any report content (graphs, marquees, cards…) overlaps the nav box
      let overlap = false;
      const nav = navRef.current;
      if (nav) {
        const nr = nav.getBoundingClientRect();
        const pad = 24;
        // main text column too close on narrower screens
        const col = document.querySelector("main .report-col");
        if (col && col.getBoundingClientRect().left < nr.right + pad) overlap = true;
        if (!overlap) {
          outer: for (const sel of CONTENT_SELECTORS) {
            for (const el of document.querySelectorAll(sel)) {
              const r = el.getBoundingClientRect();
              if (r.height > 0 && r.bottom > nr.top - pad && r.top < nr.bottom + pad && r.right > nr.left - pad && r.left < nr.right + pad) {
                overlap = true;
                break outer;
              }
            }
          }
        }
        if (!overlap) {
          outer2: for (const sel of CHART_SELECTORS) {
            for (const el of document.querySelectorAll(sel)) {
              const r = el.getBoundingClientRect();
              if (r.height > 0 && r.bottom > nr.top - pad && r.top < nr.bottom + pad) {
                overlap = true;
                break outer2;
              }
            }
          }
        }
      }
      setVisible(window.scrollY > heroEnd && !nearBottom && !overlap);
      // active section tracking
      let current = navSections[0].id;
      for (const s of navSections) {
        const el = document.getElementById(s.id);
        if (el && el.getBoundingClientRect().top <= window.innerHeight * 0.4) {
          current = s.id;
        }
      }
      setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 90, behavior: "smooth" });
    }
  };

  return (
    <nav
      ref={navRef}
      className={`fixed left-4 md:left-8 top-1/2 -translate-y-1/2 z-40 hidden lg:block transition-all duration-500 ${
        visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-4 pointer-events-none"
      }`}
      style={{ mixBlendMode: "difference" }}
      data-testid="side-nav"
      aria-label="Report sections"
    >
      <ul className="space-y-2.5">
        {navSections.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id} className="flex items-center gap-2.5">
              <span
                className="block h-px bg-white transition-all duration-300"
                style={{ width: isActive ? 22 : 0, opacity: isActive ? 1 : 0 }}
              />
              <a
                href={`#${s.id}`}
                onClick={scrollTo(s.id)}
                className={`text-[13px] transition-colors duration-200 ${
                  isActive ? "text-white font-medium" : "text-white/40 hover:text-white"
                }`}
                data-testid={`sidenav-link-${s.id}`}
              >
                {s.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
